import { useMemo } from 'react';
import { resolveScopedOverride, resolveSlotGroupFields } from '@vibesites/runtime';
import type { SlotGroupDefinition } from '@vibesites/runtime';
import { useGallery, usePropertyField, useSlotGroup, useVibesites } from '@vibesites/runtime/react';
import { pickHeroImage } from '../utils/heroImageUtils';

/**
 * Inner pages whose hero has its own component with an AI image generator.
 * Their saved background (editor pick or generated) is read site-wide so the
 * shared PageHero pages avoid repeating it.
 */
export const DEDICATED_HEROES = [
  { hint: 'floor-plans', alias: 'floor-plans', path: '/floor-plans', groupPath: 'floor-plans-hero.bg_image' },
  { hint: 'amenities', alias: 'amenities', path: '/amenities', groupPath: 'amenities-hero.bg_image' },
  { hint: 'gallery', alias: 'gallery', path: '/gallery', groupPath: 'gallery-hero.bg_image' },
  { hint: 'neighborhood', alias: 'neighborhood', path: '/neighborhood', groupPath: 'neighborhood-hero.bg_image' },
  { hint: 'virtual-tour', alias: 'virtual-tour', path: '/virtual-tour', groupPath: 'virtual-tour-hero.bg_image' },
  { hint: 'contact', alias: 'contact', path: '/contact-us', groupPath: 'contact-hero.bg_image' },
] as const;

/** Resolves only explicit overrides — no defaultField / defaultValue fallbacks. */
function overrideOnly(definition: SlotGroupDefinition): SlotGroupDefinition {
  const fields: SlotGroupDefinition['fields'] = {};
  for (const [name, field] of Object.entries(definition.fields)) {
    fields[name] = { label: field.label, type: field.type };
  }
  return { ...definition, fields, generator: undefined };
}

/**
 * Background for an inner page hero. Priority: editor / generator override,
 * then a render-time pick that is relevant to the page and unused by the
 * homepage hero or other inner heroes, then the slot group's defaults.
 */
export function useInnerHeroImage(
  groupPath: string,
  definition: SlotGroupDefinition,
  pageHint: string,
  options: { keywords?: string[]; altText?: string } = {},
): { image: string | null; alt: string } {
  const { keywords = [], altText = 'Page banner' } = options;
  const { pagePaths, property, slotGroupOverrides } = useVibesites();
  const gallery = useGallery();
  const featuredImage = usePropertyField('property_featured_image');
  const probeDef = useMemo(() => overrideOnly(definition), [definition]);
  const resolved = useSlotGroup(groupPath, definition);
  const override = useSlotGroup(groupPath, probeDef);

  const fixedImages = useMemo(() => {
    const images: Record<string, string | null | undefined> = {};
    for (const hero of DEDICATED_HEROES) {
      const saved = resolveScopedOverride(slotGroupOverrides, hero.groupPath, {
        alias: hero.alias,
        path: hero.path,
      });
      if (saved) images[hero.hint] = resolveSlotGroupFields(property, saved, probeDef).image;
    }
    return images;
  }, [slotGroupOverrides, property, probeDef]);

  const picked = useMemo(() => {
    const index = pickHeroImage(gallery, keywords, pageHint, {
      pagePaths,
      reservedImages: [featuredImage],
      fixedImages,
    });
    return index >= 0 ? gallery[index] : null;
  }, [gallery, keywords, pageHint, pagePaths, featuredImage, fixedImages]);

  const image = override.image || picked?.image || resolved.image || gallery[0]?.image || null;
  const alt =
    override.alt ||
    (image && image === picked?.image ? picked?.alt_text : null) ||
    resolved.alt ||
    altText;

  return { image, alt };
}
